import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable, OnDestroy } from '@angular/core';

import { AppMenuItem } from '@nx-ahs/shared/models';
import { AppMenuService } from '@nx-ahs/shared/services';

import { Subscription, firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';
import { UserMenuPreferencesService } from './user-menu-preferences.service';

@Injectable({
	providedIn: 'root'
})
export class UserMenuHistoryService implements OnDestroy {
	public userMenuHistoryMenuUpdated: EventEmitter<AppMenuItem[]> = new EventEmitter<AppMenuItem[]>();

	private _userMenuHistoryMenu: AppMenuItem[] = null;
	private userMenuHistories: string[] = [];
	private favoriteMenuUpdateSub: Subscription = null;
	private subscriptions: Subscription[] = [];
	private loading = false;

	public get userMenuHistoryMenu() {
		return this._userMenuHistoryMenu;
	}

	constructor(
		private http: HttpClient,
		private appMenuService: AppMenuService,
		private userMenuPreferencesService: UserMenuPreferencesService
	) {
		this.subscriptions.push(
			this.userMenuPreferencesService.userFavoritesMenuUpdated.subscribe(() => {
				this.markHistoryMenuFavorites(this._userMenuHistoryMenu);
			})
		);

		this.subscriptions.push(
			this.appMenuService.appMenuUpdated.subscribe((newMenu) => {
				this.rebuildHistoryMenu();
			})
		);
	}

	ngOnDestroy() {
		this.subscriptions.forEach((sub) => {
			sub.unsubscribe();
		});
	}

	public async logUserMenuHistory(menuItemId: string): Promise<any> {
		this.updateHistoryMenuItems(menuItemId);
		const result = await firstValueFrom(
			this.http.post<any>(environment.apiUrl + `api/Main/LogUserMenuHistory?menuItemId=${menuItemId}`, null)
		);
		return result;
	}

	public async getUserMenuHistoryItems(): Promise<AppMenuItem[]> {
		if (this._userMenuHistoryMenu) return Promise.resolve(this.userMenuHistoryMenu);

		if (!this.loading) {
			this.loading = true;
			this.userMenuHistories = await firstValueFrom(
				this.http.get<string[]>(environment.apiUrl + `api/Main/GetUserMenuHistoryItems`)
			);
			this.rebuildHistoryMenu();
			this.loading = false;
			return Promise.resolve(this.userMenuHistoryMenu);
		}
	}

	public rebuildHistoryMenu() {
		if (this.appMenuService.appMenuConfig && this.userMenuHistories.length > 0) {
			this._userMenuHistoryMenu = this.buildHistoryMenuFromMenuConfig(
				JSON.parse(JSON.stringify(this.appMenuService.appMenuConfig)),
				this.userMenuHistories
			);
			this.userMenuHistoryMenuUpdated.emit(this._userMenuHistoryMenu);
		}
	}

	private updateHistoryMenuItems(menuItemId: string): void {
		if (this.userMenuHistories.length >= 10) this.userMenuHistories.pop();

		this.userMenuHistories.unshift(menuItemId);
		this.rebuildHistoryMenu();
	}

	private buildHistoryMenuFromMenuConfig(menuConfig: AppMenuItem[], userHistoryMenuIds: string[]): AppMenuItem[] {
		const historyItemMenu: AppMenuItem[] = [];

		userHistoryMenuIds.forEach((id: string) => {
			let newMenu = undefined as AppMenuItem;
			newMenu = this.buildHistoryItem(menuConfig, id, newMenu);
			if (newMenu) {
				newMenu.childMenu = null;
				newMenu.favorited = null;
				historyItemMenu.push(newMenu);
			}
		});

		return historyItemMenu;
	}

	private buildHistoryItem(menuConfig: AppMenuItem[], userHistoryMenuId: string, newMenu: AppMenuItem): AppMenuItem {
		if (!newMenu) {
			for (let i = 0; i < menuConfig.length; i++) {
				const menuItem = menuConfig[i];
				const children = menuItem.childMenu;
				if (menuItem.menuItemId == userHistoryMenuId) {
					newMenu = JSON.parse(JSON.stringify(menuItem));
					i = menuConfig.length;
				}

				if (children && !newMenu) {
					newMenu = this.buildHistoryItem(children, userHistoryMenuId, newMenu);
				}
			}
		}
		return newMenu;
	}

	private markHistoryMenuFavorites(historyMenu: AppMenuItem[]): AppMenuItem[] {
		historyMenu?.forEach((historyMenuItem) => {
			historyMenuItem.favorited = false;
			if (this.userMenuPreferencesService.userMenuFavorites?.includes(historyMenuItem.menuItemId)) {
				historyMenuItem.favorited = true;
			}
		});

		return historyMenu;
	}
}
